import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, RotateCcw, Tags, Trash2 } from 'lucide-react'
import {
  Button, Card, CardBody, CardHeader, ErrorState, LoadingState, NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, addStockItem, deleteStockItem, fetchStockItems, formatCount, formatQuantity, may,
  saveStockItem, setStockItemActive, translateError, STOCK_UNITS,
  type StockItemKind, type StockItemRow, type StockUnit,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/**
 * **THE CODE IS SUGGESTED AND SHOWN, NEVER APPLIED SILENTLY.** It is typed once and never
 * again — every movement in the ledger will carry it, and every report groups by it — so
 * the person adding an item sees what they are committing to before they commit to it.
 * The same derivation Sprints 56, 63 and 69 use, shared by shape rather than by import
 * because each screen's field is its own.
 */
const suggestCode = (name: string) =>
  name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 24)

/* ============================================================================
   W-96 — THE THINGS A HOTEL COUNTS.  Sprint 80.

   ── THE SAME GAP W-109 FILLED, ON THE OTHER HALF OF THE VOCABULARY ─────────────
   Every screen in this area is built on a set of items **only the seed could create**.
   Eleven of them: bath towel, hand towel, sheets, pillowcase, bathrobe, bath mat, soap,
   shampoo, slippers, water. A real hotel handed this product could correct our Arabic for
   "bath towel" on W-128 and could not say *"we also issue prayer mats"* at all.

   A par belongs to a place AND an item; a linen standard is a number per item. A hotel
   that cannot create an item cannot set a par for it, cannot put it in a standard, and
   cannot count it.

   ── THE KIND IS THE ONE FIELD THAT STOPS BEING EDITABLE ────────────────────────
   `CIRCULATING` and `CONSUMABLE` decide what *issued to a room* MEANS — a towel issued is
   still the hotel's and comes back; a bar of soap is gone. Changing it after two years of
   movements does not restate history, it makes every one of those movements mean something
   different from what it meant when it was written.

   **So the screen stops offering it once the ledger has touched the row**, and says why
   rather than drawing a disabled control with no explanation. That is #144's lesson: a
   control that exists to be refused teaches people to distrust the screen.

   ── AND THERE IS NO PRICE ON THE SCREEN AT ALL ─────────────────────────────────
   This list carried a replacement-cost box, left empty while CQ-129 was open, and a line
   under the table counting how many items nobody had priced. The hotel's answer, on 20
   September, was that they keep no cost in this system — only the count. So the column,
   the counter and the value figure on the variance screen went together in Sprint 84.
   **A box nobody will ever fill is not an invitation, it is a reproach**: every person
   who opened this screen saw a column of blanks and had to learn that it did not matter.
   ========================================================================= */

export function StockItems({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const queryClient = useQueryClient()

  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [nameAr, setNameAr] = useState('')
  const [kind, setKind] = useState<StockItemKind>('CIRCULATING')
  const [unit, setUnit] = useState<StockUnit>('piece')
  const [refusal, setRefusal] = useState<string>()

  /* **THE PERMISSION IS KNOWN BEFORE THE REQUEST, SO THE REQUEST IS NOT MADE.** The same
     `enabled:` gate W-122 and W-124 use. Without it the screen asks for a list it already
     knows it may not have, and the server's refusal is the only thing that stops it —
     which is a round trip spent to be told something the session already said. */
  const mayAdminister = may('admin.stockPars')

  const items = useQuery({
    queryKey: ['stock-items', propertyId],
    queryFn: ({ signal }) => fetchStockItems(propertyId, { signal }),
    enabled: mayAdminister,
    retry: false,
  })

  const settled = (rows: StockItemRow[]) => {
    queryClient.setQueryData(['stock-items', propertyId], rows)
    /* Every one of these is a list OF these items, and a new one has to appear on them. */
    void queryClient.invalidateQueries({ queryKey: ['stock-levels', propertyId] })
    void queryClient.invalidateQueries({ queryKey: ['replenishment', propertyId] })
    void queryClient.invalidateQueries({ queryKey: ['stock-pars', propertyId] })
    void queryClient.invalidateQueries({ queryKey: ['linen-standards', propertyId] })
    setRefusal(undefined)
  }

  const refused = (e: unknown) => setRefusal(String(translateError(e, locale).message))

  const add = useMutation({
    mutationFn: () => addStockItem(propertyId, {
      code: suggestCode(name), name: name.trim(), nameAr: nameAr.trim() || null, kind, unit,
    }),
    onSuccess: (rows) => {
      settled(rows); setAdding(false); setName(''); setNameAr('')
    },
    onError: refused,
  })

  const save = useMutation({
    mutationFn: (input: {
      id: string; name: string; nameAr: string | null
      kind?: StockItemKind; unit: StockUnit
    }) => saveStockItem(propertyId, input.id, {
      name: input.name,
      nameAr: input.nameAr,
      ...(input.kind ? { kind: input.kind } : {}),
      unit: input.unit,
    }),
    onSuccess: settled,
    onError: refused,
  })

  const setActive = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      setStockItemActive(propertyId, input.id, input.isActive),
    onSuccess: settled,
    onError: refused,
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteStockItem(propertyId, id),
    onSuccess: settled,
    onError: refused,
  })

  if (!mayAdminister) return <NoPermissionState />
  if (items.isLoading && !items.error) return <LoadingState rows={5} />

  if (items.error && !items.data) {
    if (items.error instanceof ApiError && items.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(items.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={items.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const rows = items.data!

  return (
    <div className="space-y-4 p-4">
      {refusal && (
        <p role="alert" className="text-sm" style={{ color: 'var(--hk-hk-dirty-text)' }}>
          {refusal}
        </p>
      )}

      <Card>
        <CardHeader
          title={t.itTitle}
          icon={<Tags className="size-4" />}
          actions={
            <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus className="size-3.5" aria-hidden />
              {t.itAdd}
            </Button>
          }
        />

        {adding && (
          <CardBody className="space-y-2 border-b border-border">
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1 text-[12px]">
                {t.itName}
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-label={t.itName}
                  className="w-52 rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
                />
              </label>

              <label className="flex flex-col gap-1 text-[12px]">
                {t.itNameAr}
                <input
                  value={nameAr}
                  onChange={(e) => setNameAr(e.target.value)}
                  dir="rtl"
                  aria-label={t.itNameAr}
                  className="w-52 rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
                />
              </label>

              <label className="flex flex-col gap-1 text-[12px]">
                {t.itKind}
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as StockItemKind)}
                  aria-label={t.itKind}
                  className="rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
                >
                  <option value="CIRCULATING">{t.itKinds.CIRCULATING}</option>
                  <option value="CONSUMABLE">{t.itKinds.CONSUMABLE}</option>
                </select>
              </label>

              <label className="flex flex-col gap-1 text-[12px]">
                {t.itUnit}
                <select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value as StockUnit)}
                  aria-label={t.itUnit}
                  className="rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
                >
                  {STOCK_UNITS.map((u) => (
                    <option key={u} value={u}>{t.itUnits[u]}</option>
                  ))}
                </select>
              </label>

              <Button
                type="button"
                size="sm"
                disabled={name.trim() === '' || add.isPending}
                onClick={() => add.mutate()}
              >
                {t.save}
              </Button>
              <Button type="button" size="sm" variant="ghost"
                      onClick={() => { setAdding(false); setName(''); setNameAr('') }}>
                {t.cancel}
              </Button>
            </div>

            {/* **THE KIND IS SAID BEFORE IT IS CHOSEN, NOT AFTER IT IS REGRETTED.** It is
                the one field on this form that cannot be corrected once anything has
                moved, and the person adding an item has no way of knowing that. */}
            <p className="text-[12px] text-muted-foreground">{t.itKindWarning}</p>

            {/* **SHOWN, NOT APPLIED SILENTLY.** The code is typed once and never again. */}
            {name.trim() !== '' && (
              <p className="text-[12px] text-muted-foreground">
                {fill(t.itCodeWillBe, { code: suggestCode(name) })}
              </p>
            )}
          </CardBody>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                <th className="px-3 py-2 text-start font-medium">{t.itName}</th>
                <th className="px-3 py-2 text-start font-medium">{t.itKind}</th>
                <th className="px-3 py-2 text-start font-medium">{t.itUnit}</th>
                <th className="px-3 py-2 text-end font-medium">{t.itOwned}</th>
                <th className="px-3 py-2 text-end font-medium" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <ItemRow
                  key={row.stockItemId}
                  row={row}
                  busy={save.isPending || setActive.isPending || remove.isPending}
                  onSave={(next) => save.mutate({ id: row.stockItemId, ...next })}
                  onSetActive={(isActive) =>
                    setActive.mutate({ id: row.stockItemId, isActive })}
                  onRemove={() => remove.mutate(row.stockItemId)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-[12px] text-muted-foreground">{t.itNote}</p>
    </div>
  )
}

function ItemRow({
  row, busy, onSave, onSetActive, onRemove,
}: {
  row: StockItemRow
  busy: boolean
  onSave: (next: {
    name: string; nameAr: string | null
    kind?: StockItemKind; unit: StockUnit
  }) => void
  onSetActive: (isActive: boolean) => void
  onRemove: () => void
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [name, setName] = useState(row.name)
  const [nameAr, setNameAr] = useState(row.nameAr ?? '')
  const [kind, setKind] = useState<StockItemKind>(row.kind)
  const [unit, setUnit] = useState<StockUnit>(row.unit)
  /** **SETTLED BY THE FIRST MOVEMENT.** See the header. */
  const kindSettled = row.movementsEver > 0

  const dirty =
    name.trim() !== row.name
    || nameAr.trim() !== (row.nameAr ?? '')
    || unit !== row.unit
    || (!kindSettled && kind !== row.kind)

  return (
    <tr className={`border-b border-border/60 last:border-0 ${
      row.isActive ? '' : 'opacity-60'}`}
    >
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label={`${row.code} — ${t.itName}`}
            className="w-40 rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
          />
          <input
            value={nameAr}
            onChange={(e) => setNameAr(e.target.value)}
            dir="rtl"
            aria-label={`${row.code} — ${t.itNameAr}`}
            className="w-40 rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
          />
          {dirty && (
            <Button
              type="button"
              size="sm"
              disabled={busy || name.trim() === ''}
              onClick={() => onSave({
                name: name.trim(),
                nameAr: nameAr.trim() || null,
                /* **OMITTED WHERE IT IS SETTLED**, rather than sent and refused. */
                ...(kindSettled ? {} : { kind }),
                unit,
              })}
            >
              {t.save}
            </Button>
          )}
        </div>
        <span className="mt-0.5 block text-[11px] text-muted-foreground">
          <span dir="ltr">{row.code}</span>
          {row.standardsSet > 0 && <>{' · '}{fill(t.itInStandards, {
            count: formatCount(row.standardsSet, locale),
          })}</>}
          {!row.isActive && <>{' · '}{t.itRetired}</>}
        </span>
      </td>

      <td className="px-3 py-2">
        {kindSettled ? (
          /* **NOT A DISABLED SELECT.** A greyed control with no explanation is read as
             broken; a sentence saying the ledger has settled it is read as a rule. */
          <>
            <span className="block">{t.itKinds[row.kind]}</span>
            <span className="block text-[11px] text-muted-foreground">{t.itKindSettled}</span>
          </>
        ) : (
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as StockItemKind)}
            aria-label={`${row.code} — ${t.itKind}`}
            className="rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
          >
            <option value="CIRCULATING">{t.itKinds.CIRCULATING}</option>
            <option value="CONSUMABLE">{t.itKinds.CONSUMABLE}</option>
          </select>
        )}
      </td>

      <td className="px-3 py-2">
        <select
          value={unit}
          onChange={(e) => setUnit(e.target.value as StockUnit)}
          aria-label={`${row.code} — ${t.itUnit}`}
          className="rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
        >
          {STOCK_UNITS.map((u) => (
            <option key={u} value={u}>{t.itUnits[u]}</option>
          ))}
        </select>
      </td>

      <td className="px-3 py-2 text-end tnum text-muted-foreground">
        {formatQuantity(row.quantityOwned, locale)}
      </td>

      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1.5">
          <Button type="button" size="sm" variant="ghost" disabled={busy}
                  onClick={() => onSetActive(!row.isActive)}>
            {row.isActive
              ? t.itRetire
              : <><RotateCcw className="size-3.5" aria-hidden />{t.itRestore}</>}
          </Button>

          {/* **DRAWN ONLY WHERE IT WOULD WORK.** The row carries its own movement count
              for exactly this — #144's lesson applied to an administration list. */}
          {row.movementsEver === 0 ? (
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onRemove}>
              <Trash2 className="size-3.5" aria-hidden />
              {t.itRemove}
            </Button>
          ) : (
            <span className="text-[11.5px] text-muted-foreground">
              {fill(t.itInLedger, { count: formatCount(row.movementsEver, locale) })}
            </span>
          )}
        </div>
      </td>
    </tr>
  )
}
